import { IDynamicFormWidgetSection } from '@components/dynamic-form/types';
import {
  DynamicFormWidgetInputType,
  FormFieldType
} from '@components/dynamic-form/types/inputTypes';
import { getLineCategory, hasIndividualType } from '@utils/helper-utils';
import { REGEX } from '@utils/validationUtils';

import { LineCategory ,PhoneType } from 'src/enums';

export const initialValues = {
  type: '',
  title: '',
  firstName: '',
  middleName: '',
  lastName: '',
  dateOfBirth: '',
  gender: '',
  nationality: '',
  companyName: '',
  email: '',
  phoneType: PhoneType.MOBILE,
  phoneNumber: '',
  isTaxable: true,
  occupationClass: '',
  profession: '',
  employer: '',
  dateOfEmployment: '',
  addressType: '',
  country: '',
  zone: '',
  city: '',
  street: '',
  building: '',
  addressDetails: '',
  addressDetailsArabic: ''
};

export const getInputs = (
  values: Record<string, any> = initialValues,
  lovs: Record<string, Record<string, any>> = {},
  lineCode: string | number | undefined,
  section: string
): IDynamicFormWidgetSection[] => {
  const line = String(lineCode ?? '');
  const isIndividualOnlyLine =
    section === 'insured' && hasIndividualType(line);
  const isPerson = values.type === 'INDIVIDUAL' || isIndividualOnlyLine;
  const isCompany = values.type === 'COMPANY' && !isIndividualOnlyLine;
  const isTravel = getLineCategory(line) === LineCategory.Travel;
  const isInsured = section === 'insured';

  // Profession is a choice (from OccupationList) and Occupation Class is
  // read-only, auto-derived from the selected Profession. Shown for individuals.
  const professionField: DynamicFormWidgetInputType = {
    name: 'profession',
    title: 'Profession',
    placeholder: 'Profession',
    type: FormFieldType.select,
    value: values.profession,
    hidden: !isPerson,
    selectOptions: lovs.occupationList
  };

  const occupationClassField: DynamicFormWidgetInputType = {
    name: 'occupationClass',
    title: 'Occupation Class',
    placeholder: 'Occupation Class',
    type: FormFieldType.select,
    value: values.occupationClass,
    hidden: !isPerson,
    // Derived from the selected Profession; never user-editable.
    disabled: true,
    selectOptions: lovs.occupationClasses
  };

  return [
    {
      sectionTitle: 'Person Details',
      inputs: {
        type: {
          name: 'type',
          title: 'Type',
          type: FormFieldType.select,
          placeholder: 'Type',
          value: isIndividualOnlyLine ? 'INDIVIDUAL' : values.type,
          required: true,
          disabled: isIndividualOnlyLine,
          selectOptions: lovs.PersonTypes
        },
        title: {
          name: 'title',
          title: 'Title',
          placeholder: 'Title',
          type: FormFieldType.select,
          value: values.title,
          required: isPerson,
          hidden: !isPerson,
          selectOptions: lovs.titles
        },
        firstName: {
          name: 'firstName',
          title: 'First Name',
          placeholder: 'First Name',
          type: FormFieldType.text,
          value: values.firstName,
          required: isPerson,
          hidden: !isPerson
        },
        middleName: {
          name: 'middleName',
          title: 'Middle Name',
          placeholder: 'Middle Name',
          type: FormFieldType.text,
          value: values.middleName,
          required: isPerson,
          hidden: !isPerson
        },
        lastName: {
          name: 'lastName',
          title: 'Last Name',
          placeholder: 'Last Name',
          type: FormFieldType.text,
          value: values.lastName,
          required: isPerson,
          hidden: !isPerson
        },
        dateOfBirth: {
          name: 'dateOfBirth',
          title: 'Date of Birth',
          placeholder: 'Date of Birth',
          type: FormFieldType.date,
          value: values.dateOfBirth,
          hidden: !isPerson,
          maxDate: new Date(),
          required: true
        },
        gender: {
          name: 'gender',
          title: 'Gender',
          placeholder: 'Gender',
          type: FormFieldType.select,
          value: values.gender,
          hidden: !isPerson,
          selectOptions: lovs.genders,
          required: isInsured && isTravel
        },
        nationality: {
          name: 'nationality',
          title: 'Nationality',
          placeholder: 'Nationality',
          type: FormFieldType.select,
          value: values.nationality,
          hidden: !isPerson,
          selectOptions: lovs.nationalities
        },
        companyName: {
          name: 'companyName',
          title: 'Company Name',
          placeholder: 'Company Name',
          type: FormFieldType.text,
          value: values.companyName,
          hidden: !isCompany,
          required: isCompany
        },
        email: {
          name: 'email',
          title: 'Email',
          placeholder: 'Email',
          type: FormFieldType.text,
          value: values.email,
          customValidator: (values) => {
            if (values.email && !REGEX.email.test(values.email)) {
              return 'Invalid Email Address';
            }

            return '';
          },
          required: false
        },
        phoneType: {
          name: 'phoneType',
          title: 'Phone Type',
          placeholder: 'Phone Type',
          type: FormFieldType.select,
          value: values.phoneType,
          required: true,
          selectOptions: lovs.phoneType
        },
        phoneNumber: {
          name: 'phoneNumber',
          title: 'Phone Number',
          placeholder: 'Phone Number',
          type: FormFieldType.phoneNumber,
          value: values.phoneNumber,
          required: false
        }
      }
    },
    {
      sectionTitle: 'Business Details',
      inputs: {
        isTaxable: {
          name: 'isTaxable',
          title: 'Is Taxable',
          placeholder: 'Is Taxable',
          type: FormFieldType.switch,
          value: values.isTaxable
        },
        // Profession (choice) before Occupation Class.
        profession: professionField,
        occupationClass: occupationClassField,
        employer: {
          name: 'employer',
          title: 'Employer/Institute',
          placeholder: 'Employer/Institute',
          type: FormFieldType.text,
          value: values.employer,
          hidden: !isPerson
        },
        dateOfEmployment: {
          name: 'dateOfEmployment',
          title: 'Date of Employment',
          placeholder: 'Date of Employment',
          type: FormFieldType.date,
          value: values.dateOfEmployment,
          hidden: !isPerson
        }
      }
    },
    {
      sectionTitle: 'Additional Details',
      hidden: !isTravel || !isPerson,
      inputs: {
        passportNumber: {
          name: 'passportNumber',
          title: 'Passport Number',
          placeholder: 'Passport Number',
          type: FormFieldType.text,
          value: values.passportNumber,
          required: isInsured && isTravel
        }
      }
    },
    {
      sectionTitle: 'Primary Address Details',
      inputs: {
        addressType: {
          name: 'addressType',
          title: 'Address Type',
          placeholder: 'Select Address Type',
          type: FormFieldType.select,
          value: values.addressType,
          required: true,
          selectOptions: lovs.addressType
        },
        country: {
          name: 'country',
          title: 'Country',
          placeholder: 'Country',
          type: FormFieldType.select,
          value: values.country,
          required: true,
          selectOptions: lovs.countries
        },
        zone: {
          name: 'zone',
          title: 'Zone',
          placeholder: 'Zone',
          type: FormFieldType.select,
          value: values.zone,
          hidden: values.country !== 'LB',
          selectOptions: values.country === 'LB' ? lovs.zones : {}
        },
        city: {
          name: 'city',
          title: 'City',
          placeholder: 'City',
          type: FormFieldType.text,
          value: values.city,
          required: true
        },
        street: {
          name: 'street',
          title: 'Street',
          placeholder: 'Street',
          type: FormFieldType.text,
          value: values.street
        },
        building: {
          name: 'building',
          title: 'Building',
          placeholder: 'Building',
          type: FormFieldType.text,
          value: values.building
        },
        addressDetails: {
          name: 'addressDetails',
          title: 'Address Details',
          placeholder: 'Address Details',
          type: FormFieldType.text,
          value: values.addressDetails
        },
        addressDetailsArabic: {
          name: 'addressDetailsArabic',
          title: 'Address Details Arabic',
          placeholder: 'Address Details in Arabic',
          type: FormFieldType.text,
          value: values.addressDetailsArabic
        }
      }
    }
  ];
};
